/**
 * WhatsNextHero — RC7 · M8 · Ship-Time Home hero.
 *
 * Mounted at the top of MobileToday. Renders three canonical clocks
 * (Ship Time · Home Time · Port Local) and pinpoints the single next
 * actionable item on the voyage — with a live countdown when possible.
 *
 * PO decisions ratified 2026-02-17 (see PRD Section RC7-M8):
 *   1a — additive hero card at the top of Today (MobileToday unchanged below)
 *   2a — three-clock strip lives inside the hero card
 *   3a — offline fallback reads from the M4 offline pack via `readCachedToday`
 *   4a — Backend + Frontend testing agents.
 *
 * Data contract:
 *   props.today       →  canonical /api/cruises/current/today payload
 *   props.shipTime    →  canonical /api/ship-time/{voyage_id} envelope (optional)
 *   props.online      →  boolean · used to render the "showing saved
 *                        information" chip on the offline path
 *
 * No new server endpoint. Never fabricates a next event. Never fabricates a
 * clock — an unresolvable clock is rendered as "—" and labelled honestly.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Clock, Anchor, MapPin, Plane, Utensils, Compass, WifiOff, Sparkles,
} from "lucide-react";

const NAVY = "#0F2244";
const GOLD = "#C28E42";

// Canonical labels — identical to MobileToday's dictionary. If either
// diverges the underlying `time_context.context` still identifies the
// authority; the label is presentation-only.
const CONTEXT_LABEL = {
  ship: "Ship Time",
  port: "Port Local",
  home: "Home Time",
  utc:  "UTC",
};

const KIND_ICON = {
  flight: Plane,
  transfer: Plane,
  dining: Utensils,
  excursion: Compass,
  port_call: MapPin,
  voyage_event: Anchor,
};

function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return "—"; }
}

/** Return `{value, unit}` for the smallest useful unit above 0. */
function humanCountdown(deltaMs) {
  const abs = Math.max(0, deltaMs);
  const s = Math.floor(abs / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0)  return { value: d, unit: d === 1 ? "day"    : "days" };
  if (h > 0)  return { value: h, unit: h === 1 ? "hour"   : "hours" };
  if (m > 0)  return { value: m, unit: m === 1 ? "minute" : "minutes" };
  return { value: Math.max(0, s), unit: "seconds" };
}

/**
 * Choose the "what's next" event.
 *
 * Priority ladder (all canonical fields — never fabricated):
 *   1. First future event in `plan[]` whose `time_context.instant_utc`
 *      is in the future (relative to Date.now() — the ONE javascript
 *      clock we're allowed to consult).
 *   2. Fallback to the composed `time.next_event` from `/today`.
 *   3. Fallback to the next voyage-timeline day whose `date_iso` is in
 *      the future (embark day, next port day, etc.).
 *   4. Nothing → return null.
 *
 * We never return a past event as "next".
 */
function pickNextEvent(today) {
  if (!today) return null;
  const nowMs = Date.now();
  // 1. plan events.
  const plan = Array.isArray(today.plan) ? today.plan : [];
  const futures = plan
    .map((e) => {
      const iso = e?.time_context?.instant_utc;
      if (!iso) return null;
      const t = Date.parse(iso);
      if (isNaN(t) || t < nowMs) return null;
      return { event: e, ms: t, source: "plan" };
    })
    .filter(Boolean)
    .sort((a, b) => a.ms - b.ms);
  if (futures[0]) return futures[0];

  // 2. time.next_event (server-supplied composed pointer).
  const ne = today?.time?.next_event;
  if (ne?.instant_utc) {
    const t = Date.parse(ne.instant_utc);
    if (!isNaN(t) && t >= nowMs) {
      return {
        event: {
          id: ne.id || "next_event",
          kind: ne.kind || "voyage_event",
          title: ne.title || "Upcoming",
          time_context: {
            instant_utc: ne.instant_utc,
            instant_display: ne.instant_display,
            context: ne.context || "ship",
          },
        },
        ms: t,
        source: "time.next_event",
      };
    }
  }

  // 3. voyage_timeline first upcoming day.
  const tl = today?.voyage_timeline?.days || [];
  for (const day of tl) {
    if (!day?.date_iso) continue;
    const t = Date.parse(`${day.date_iso}T00:00:00Z`);
    if (isNaN(t)) continue;
    if (t >= nowMs) {
      return {
        event: {
          id: `voy_day_${day.day}`,
          kind: day.kind === "sea_day" ? "voyage_event" : "port_call",
          title: day.title || `Day ${day.day}`,
          time_context: {
            instant_utc: `${day.date_iso}T00:00:00Z`,
            instant_display: day.date_label_long || day.date_label || day.date_iso,
            context: "ship",
          },
        },
        ms: t,
        source: "voyage_timeline",
      };
    }
  }
  return null;
}


function ClockCell({ label, iso, isPrimary, context }) {
  return (
    <div
      data-testid={`whats-next-clock-${context}`}
      className={`flex-1 min-w-0 rounded-xl px-3 py-2 border ${
        isPrimary ? "border-white/25 bg-white/10" : "border-white/15 bg-white/5"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/70">
        {label}
      </div>
      <div className="text-base font-semibold text-white leading-tight">
        {fmtClock(iso)}
      </div>
    </div>
  );
}


export default function WhatsNextHero({ today, shipTime, online = true }) {
  const [nowTick, setNowTick] = useState(Date.now());
  // Refresh countdown once per second while a next event is queued.
  const next = useMemo(() => pickNextEvent(today), [today]);
  useEffect(() => {
    if (!next?.ms) return undefined;
    const int = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(int);
  }, [next?.ms]);

  // Clocks. Prefer canonical `/ship-time` envelope (flat ISO strings
  // — see MobileToday.jsx:317-319 for the same shape); fall back to
  // the composed `/today.time` block which is what the offline pack
  // holds.
  const shipISO =
    shipTime?.ship ||
    today?.time?.ship_time?.iso ||
    null;
  const homeISO =
    shipTime?.home ||
    today?.time?.home_time?.iso ||
    null;
  const portISO =
    shipTime?.port ||
    today?.time?.port_time?.iso ||
    null;

  const stage = today?.overall_stage || null;
  const stageChip =
    stage === "pre-voyage" ? "PRE-VOYAGE"
      : stage === "port-day" ? "PORT DAY"
        : stage === "sea-day" ? "SEA DAY"
          : stage === "embark" ? "EMBARK DAY"
            : stage === "disembark" ? "DISEMBARK"
              : stage === "post-voyage" ? "COMPLETE"
                : "VOYAGE";

  const dayLabel = today?.day?.label || null;
  const Icon = next?.event?.kind ? (KIND_ICON[next.event.kind] || Compass) : Sparkles;
  const countdown = next?.ms ? humanCountdown(next.ms - nowTick) : null;
  const dayProgress =
    typeof today?.day?.progress === "number" ? today.day.progress : null;

  return (
    <section
      data-testid="whats-next-hero"
      data-stage={stage || ""}
      className="mx-4 mt-4 rounded-3xl overflow-hidden"
      style={{
        background: `linear-gradient(155deg, ${NAVY} 0%, #17335e 50%, #204a86 100%)`,
      }}
    >
      {/* Header row · stage chip + offline pill */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-2">
        <span
          data-testid="whats-next-stage-chip"
          className="text-[10px] tracking-[0.14em] font-semibold uppercase px-2 py-0.5 rounded-full"
          style={{ color: GOLD, background: "rgba(194,142,66,0.14)", border: `1px solid ${GOLD}55` }}
        >
          {stageChip}
        </span>
        {!online && (
          <span
            data-testid="whats-next-offline-chip"
            className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full text-amber-100 bg-amber-500/20 border border-amber-300/40"
          >
            <WifiOff className="w-3 h-3" /> Saved
          </span>
        )}
      </div>

      {/* What's next — the headline */}
      <div className="px-5 pb-4">
        {next ? (
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/12 flex items-center justify-center text-white shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div
                data-testid="whats-next-label"
                className="text-[11px] tracking-[0.12em] uppercase text-white/70 font-semibold"
              >
                What's next
              </div>
              <div
                data-testid="whats-next-title"
                className="text-white text-xl font-bold leading-tight truncate"
              >
                {next.event.title}
              </div>
              <div className="mt-1 text-xs text-white/75 flex items-center gap-2 flex-wrap">
                {next.event.time_context?.instant_display && (
                  <span data-testid="whats-next-when">
                    {next.event.time_context.instant_display}
                  </span>
                )}
                {CONTEXT_LABEL[next.event.time_context?.context] && (
                  <span className="text-white/55">
                    · {CONTEXT_LABEL[next.event.time_context.context]}
                  </span>
                )}
              </div>
              {countdown && (
                <div
                  data-testid="whats-next-countdown"
                  className="mt-2 inline-flex items-baseline gap-1.5 text-white bg-white/8 border border-white/15 rounded-full px-2.5 py-1"
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-lg font-bold leading-none">{countdown.value}</span>
                  <span className="text-[11px] tracking-wider uppercase text-white/75">
                    {countdown.unit}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            data-testid="whats-next-empty"
            className="flex items-start gap-3"
          >
            <div className="w-11 h-11 rounded-2xl bg-white/12 flex items-center justify-center text-white shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] tracking-[0.12em] uppercase text-white/70 font-semibold">
                What's next
              </div>
              <div className="text-white text-lg font-bold leading-tight">
                Enjoy your voyage.
              </div>
              <div className="text-xs text-white/70 mt-0.5">
                {dayLabel || "No upcoming events on file."}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Three-clock strip */}
      <div
        data-testid="whats-next-clocks"
        className="px-5 pb-5 pt-1 flex items-stretch gap-2"
      >
        <ClockCell label="Ship Time"  iso={shipISO} isPrimary={true}  context="ship" />
        <ClockCell label="Home Time"  iso={homeISO} isPrimary={false} context="home" />
        <ClockCell label="Port Local" iso={portISO} isPrimary={false} context="port" />
      </div>

      {/* Optional day-progress hairline */}
      {dayProgress !== null && dayProgress > 0 && dayProgress <= 1 && (
        <div className="h-1 w-full bg-white/8">
          <div
            data-testid="whats-next-day-progress"
            className="h-1"
            style={{
              width: `${Math.round(dayProgress * 100)}%`,
              background: GOLD,
            }}
          />
        </div>
      )}
    </section>
  );
}

export { pickNextEvent, humanCountdown };
